import type { EventBinding } from "../api/events";
import type { EventStore } from "./event-operation-store";
const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export const auctionOperationTypes = [
  "auction.direct.create",
  "auction.follow-on.create",
  "auction.draft.save",
  "auction.publish",
  "auction.cancel",
] as const;
export interface AuctionIntent {
  operationType: (typeof auctionOperationTypes)[number];
  auctionId?: string;
  eventId?: string;
  expectedVersion: string;
  sourcingVersion?: string;
  publicationId?: string;
}
export interface AuctionOperation extends AuctionIntent {
  idempotencyKey: string;
  startedAt: number;
}
export function auctionStorageKey(binding: EventBinding) {
  if (
    binding.audience !== "workspace" ||
    !UUID.test(binding.tenantId) ||
    !UUID.test(binding.accountId)
  )
    throw new Error("Invalid auction authority binding");
  return `wio-veyra.auction-operation.v1:${binding.tenantId}:${binding.accountId}`;
}
function project(value: unknown): AuctionOperation {
  const x = value as Partial<AuctionOperation> | null;
  const positive = (v: string | undefined) => /^[1-9]\d{0,17}$/.test(v ?? "");
  if (
    !x ||
    !auctionOperationTypes.includes(x.operationType!) ||
    !/^auction:[a-z0-9]+:[0-9a-f]{36}$/.test(x.idempotencyKey ?? "") ||
    typeof x.startedAt !== "number" ||
    !Number.isFinite(x.startedAt) ||
    x.startedAt < 0
  )
    throw new Error("Invalid auction recovery locator");
  const direct = x.operationType === "auction.direct.create",
    follow = x.operationType === "auction.follow-on.create";
  if (
    x.operationType === "auction.cancel"
      ? !UUID.test(x.publicationId ?? "")
      : x.publicationId !== undefined
  )
    throw new Error("Mismatched auction publication locator");
  if (
    direct
      ? x.eventId !== undefined ||
        x.auctionId !== undefined ||
        x.expectedVersion !== "0" ||
        (x.sourcingVersion !== undefined && x.sourcingVersion !== "3")
      : follow
        ? !UUID.test(x.eventId ?? "") ||
          x.auctionId !== undefined ||
          x.expectedVersion !== "0" ||
          !positive(x.sourcingVersion)
        : !UUID.test(x.eventId ?? "") ||
          !UUID.test(x.auctionId ?? "") ||
          !positive(x.expectedVersion) ||
          !positive(x.sourcingVersion)
  )
    throw new Error("Mismatched auction operation target");
  return {
    operationType: x.operationType!,
    auctionId: x.auctionId,
    eventId: x.eventId,
    expectedVersion: x.expectedVersion!,
    sourcingVersion: x.sourcingVersion,
    publicationId: x.publicationId,
    idempotencyKey: x.idempotencyKey!,
    startedAt: x.startedAt,
  };
}
export function readAuctionOperation(store: EventStore, binding: EventBinding) {
  const raw = store.getItem(auctionStorageKey(binding));
  return raw === null ? undefined : project(JSON.parse(raw));
}
export function persistAuctionOperation(
  store: EventStore,
  binding: EventBinding,
  value: AuctionOperation,
) {
  const previous = readAuctionOperation(store, binding),
    row = project(value);
  if (previous && previous.idempotencyKey !== row.idempotencyKey)
    throw new Error("Unresolved auction command");
  const key = auctionStorageKey(binding),
    text = JSON.stringify(row);
  store.setItem(key, text);
  if (store.getItem(key) !== text)
    throw new Error("Auction recovery unavailable");
}
export function removeAuctionOperation(
  store: EventStore,
  binding: EventBinding,
  commandKey: string,
) {
  const row = readAuctionOperation(store, binding);
  if (row && row.idempotencyKey !== commandKey)
    throw new Error("Mismatched auction command");
  const key = auctionStorageKey(binding);
  store.removeItem(key);
  if (store.getItem(key) !== null)
    throw new Error("Auction recovery unavailable");
}
export function auctionCompletion(value: unknown, operation: AuctionOperation) {
  if (!value || typeof value !== "object") return undefined;
  const x = value as Record<string, unknown>,
    field = (key: string) => x[key] ?? x[key[0].toUpperCase() + key.slice(1)];
  if (operation.operationType === "auction.cancel") {
    if (
      field("eventId") !== operation.eventId ||
      field("auctionId") !== operation.auctionId ||
      field("publicationId") !== operation.publicationId ||
      !UUID.test(String(field("cancellationId")))
    )
      return undefined;
    return operation.auctionId;
  }
  const direct = operation.operationType === "auction.direct.create",
    follow = operation.operationType === "auction.follow-on.create";
  const returnedSource = String(field("sourcingVersion"));
  if (!/^[1-9]\d{0,17}$/.test(returnedSource)) return undefined;
  // A follow-on command may also materialize the elapsed source window. Bind to the
  // original event and require advancement, rather than guessing its internal touch count.
  const sourceMatches = direct
    ? returnedSource === (operation.sourcingVersion ?? "2")
    : follow
      ? BigInt(returnedSource) > BigInt(operation.sourcingVersion!)
      : BigInt(returnedSource) ===
        BigInt(operation.sourcingVersion!) + BigInt(1);
  if (
    !UUID.test(String(field("auctionId"))) ||
    !UUID.test(String(field("eventId"))) ||
    (operation.auctionId && operation.auctionId !== field("auctionId")) ||
    (operation.eventId && operation.eventId !== field("eventId")) ||
    String(field("version")) !==
      String(BigInt(operation.expectedVersion) + BigInt(1)) ||
    !sourceMatches ||
    (operation.operationType === "auction.publish"
      ? !UUID.test(String(field("publicationId")))
      : field("publicationId") != null)
  )
    return undefined;
  return String(field("auctionId"));
}
